import type { ComponentType } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { Lock, type LucideIcon } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { EmptyState } from "../../ui";

/**
 * One entry of a Settings subsection's side menu.
 *
 * The registry carries the panel itself, not just its label, so the menu and
 * the routes are read from the same list. That is what keeps a permission
 * honest: a tab that is filtered out of the menu has no route either, instead
 * of staying reachable by typing the URL.
 */
export interface SubsectionTab {
  /** URL segment under the subsection's base path. */
  path: string;
  /** Suffix under the subsection's i18n prefix, for the label and the hint line. */
  key: string;
  icon: LucideIcon;
  /** The platform permission the tab needs, from `./perms`. Never optional:
   *  "anyone who can open Settings" was only ever true while Settings itself
   *  sat behind one register-shaped key. */
  perm: string;
  panel: ComponentType;
}

/** The tabs this staff member may see - menu entries and routes both come from here. */
export function visibleTabs(tabs: readonly SubsectionTab[], user: StaffUser): SubsectionTab[] {
  return tabs.filter((tab) => userCanPlatform(user, tab.perm));
}

/** A path this subsection used to own, and where it lives now. */
export interface MovedPath {
  /** The old URL segment under this subsection's base. */
  from: string;
  /** Absolute path it moved to. */
  to: string;
}

/**
 * A menu entry is `.crm-snav`, this menu's own control - see `index.css` for
 * why it is no longer the toolbar's outlined pill: an idle entry carries no
 * ring, and the ring belongs to the open one alone.
 *
 * Deliberately NOT `.crm-nav`, which is what the app sidebar wears and would be
 * the obvious borrow: `html.crm-mini .crm-nav` shrinks every `.crm-nav` on the
 * page to a 44px circle and hides its label, so collapsing the sidebar would
 * have silently turned this menu into a column of unlabelled discs.
 *
 * `is-on` fills the entry rather than inverting it. The tab strip above already
 * carries the inverted pill for the section itself, and two black fills on one
 * screen read as two sections rather than as a section and its page.
 */
const itemClass = ({ isActive }: { isActive: boolean }) =>
  cn("crm-snav shrink-0 lg:w-full", isActive && "is-on");

/**
 * A Settings tab that is really a shelf of pages: a side menu on the left, one
 * routed panel on the right.
 *
 * Integrations and Phone are both this shape, and were the same file until the
 * phone stack outgrew the shelf it was sharing. Nothing here knows which of
 * them it is drawing - the tab list, the base path and the i18n prefix all come
 * from the caller, so a third shelf costs a registry and nothing else.
 */
export function SettingsSubsection({
  base,
  tabs: registry,
  user,
  navLabel,
  i18nPrefix,
  moved = [],
}: {
  /** Absolute path this subsection is mounted at, without a trailing slash. */
  base: string;
  tabs: readonly SubsectionTab[];
  user: StaffUser;
  /** The menu's accessible name - the tab's own label. */
  navLabel: string;
  /** Catalogue prefix holding `<key>.label` and `<key>.desc` for every tab. */
  i18nPrefix: string;
  /** URLs this subsection used to answer for, kept alive as redirects. */
  moved?: readonly MovedPath[];
}) {
  const t = useT();
  const tabs = visibleTabs(registry, user);
  const home = tabs[0] ? `${base}/${tabs[0].path}` : null;

  if (home === null) {
    // Unreachable through the tab strip - a subsection is only mounted when the
    // staff member holds at least one of its tabs' rights. It is here because a
    // permission removed while the page is open must not render a blank column.
    return (
      <div className="crm-card">
        <EmptyState icon={Lock} title={t("settings.noAccessTitle")} body={t("settings.noAccess")} />
      </div>
    );
  }

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[214px_1fr]">
      {/* A horizontal scroller on narrow screens, a column on wide ones. Pinned
          to the top of the content column, which is what `--crm-nav-top`
          measures - scrolling a long credentials form must not scroll away the
          menu you leave it with. */}
      <nav
        aria-label={navLabel}
        className="flex gap-1.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] lg:sticky lg:top-[var(--crm-nav-top)] lg:flex-col lg:overflow-visible [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <NavLink key={tab.path} to={`${base}/${tab.path}`} className={itemClass}>
              <Icon className="mt-[3px] size-4 shrink-0" />
              <span className="flex min-w-0 flex-col gap-0.5">
                {/* One line in the horizontal strip, free to wrap in the 214px
                    column - a two-word label is not worth widening every entry
                    for. */}
                <span className="whitespace-nowrap text-[12.5px] font-[650] lg:whitespace-normal">
                  {t(`${i18nPrefix}.${tab.key}.label`)}
                </span>
                {/* The hint only earns its line where there is room for it; in
                    the phone's horizontal strip the label is the whole entry. */}
                <span className="hidden text-[11px] font-[550] leading-snug opacity-70 lg:block">
                  {t(`${i18nPrefix}.${tab.key}.desc`)}
                </span>
              </span>
            </NavLink>
          );
        })}
      </nav>

      <div className="min-w-0">
        <Routes>
          <Route index element={<Navigate to={home} replace />} />
          {tabs.map(({ path, panel: Panel }) => (
            <Route key={path} path={path} element={<Panel />} />
          ))}
          {/* Pages that moved out of this subsection. A settings page is worth
              being able to send, so the link somebody pasted into a chat last
              month has to keep landing on the panel rather than on whatever
              tab happens to be first now. */}
          {moved.map(({ from, to }) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}
          {/* An unknown - or no longer permitted - tab lands on the first one
              rather than on the CRM dashboard, which is where the Settings
              catch-all above would otherwise throw it. */}
          <Route path="*" element={<Navigate to={home} replace />} />
        </Routes>
      </div>
    </div>
  );
}
